import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

import { ToastComponent } from './core/components/toast/toast.component';
import { IdleService } from './core/auth/services/idle.service';

@Component({
  selector: 'app-root',
  imports: [IonApp, IonRouterOutlet, ToastComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
  private readonly idle = inject(IdleService);

  private appListenerHandles: Array<{ remove: () => Promise<void> }> = [];
  private visibilityHandler: (() => void) | null = null;

  async ngOnInit(): Promise<void> {
    try {
      await this.idle.init();
    } catch {
      // ignore init failure to avoid white screen
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const h1 = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            (this.idle as unknown as { resume: () => void }).resume();
          } else {
            (this.idle as unknown as { pause: () => void }).pause();
          }
        });
        this.appListenerHandles.push(h1);
      } catch {
        // ignore listener failure
      }
      try {
        const h2 = await CapacitorApp.addListener('pause', () => {
          (this.idle as unknown as { pause: () => void }).pause();
        });
        this.appListenerHandles.push(h2);
      } catch {
        // ignore
      }
      try {
        const h3 = await CapacitorApp.addListener('resume', () => {
          (this.idle as unknown as { resume: () => void }).resume();
        });
        this.appListenerHandles.push(h3);
      } catch {
        // ignore
      }
    } else {
      this.visibilityHandler = () => {
        if (document.hidden) {
          (this.idle as unknown as { pause: () => void }).pause();
        } else {
          (this.idle as unknown as { resume: () => void }).resume();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  ngOnDestroy(): void {
    for (const h of this.appListenerHandles) {
      try {
        void h.remove().catch(() => {});
      } catch {
        // ignore
      }
    }
    this.appListenerHandles = [];
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    try {
      (this.idle as unknown as { stop: () => void }).stop();
    } catch {
      // ignore
    }
  }
}
